import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Home, Briefcase, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useEntity } from "@/context/EntityContext";

/**
 * Two-step first-run modal.
 *   Step 1: pick edition (Personal / Business).
 *   Step 2 (Business only): pick an industry so the Chart of Accounts is
 *          seeded with the right template.
 * Data is untouched — this only sets the workspace_type + optional industry.
 */
export function WorkspaceTypePicker({ open, entity }) {
  const { refresh } = useEntity();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("edition"); // "edition" | "industry"
  const [industries, setIndustries] = useState([]);

  // Preload the industry list (cheap: no auth-scoped data, just a static list)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/industries");
        setIndustries(data);
      } catch { /* silently fall back — user can still skip */ }
    })();
  }, [open]);

  const finish = async ({ workspace_type, industry }) => {
    if (!entity?.id) return;
    setBusy(true);
    try {
      const patch = { workspace_type };
      if (industry) patch.industry = industry;
      await api.patch(`/entities/${entity.id}`, patch);
      // If an industry was chosen, seed the CoA now so first-time users see
      // it immediately when they open Accounting.
      if (industry) {
        try { await api.post("/coa/apply-industry", { industry }); } catch { /* CoA may already be seeded — ignore */ }
      }
      toast.success(workspace_type === "personal" ? "Personal edition ready" : `Business edition ready · ${industries.find(i => i.key === industry)?.label || "generic"}`);
      await refresh();
      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save workspace type");
      setBusy(false);
    }
  };

  const pickEdition = (workspace_type) => {
    if (workspace_type === "personal") {
      finish({ workspace_type });
    } else {
      setStep("industry");
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-3xl" data-testid="workspace-type-picker"
                     onEscapeKeyDown={(e) => e.preventDefault()}
                     onPointerDownOutside={(e) => e.preventDefault()}>
        {step === "edition" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Welcome to Accountrix · Pick your edition</DialogTitle>
              <DialogDescription>
                Choose how <span className="font-semibold">{entity?.name || "this workspace"}</span> should behave. This only changes the sidebar and available tools — your data stays exactly where it is, and you can switch anytime in Settings.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <button type="button" disabled={busy}
                      data-testid="workspace-type-personal"
                      onClick={() => pickEdition("personal")}
                      className="group text-left rounded-2xl border-2 border-[var(--border)] hover:border-[var(--brand)] hover:bg-[var(--brand-light)] p-6 transition-colors disabled:opacity-60">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-[var(--brand-light)] group-hover:bg-white text-[var(--brand)] flex items-center justify-center">
                    <Home className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted-text)]">Edition</div>
                    <div className="text-lg font-display">Personal</div>
                  </div>
                </div>
                <p className="text-sm text-[var(--muted-text)] mb-3 leading-relaxed">
                  Everyday money management — track spending, set budgets and goals, watch net worth grow.
                </p>
                <ul className="text-xs text-[var(--text)] space-y-1.5">
                  <li>• Transactions, Categories, Rules</li>
                  <li>• Budgets · Goals · Investments</li>
                  <li>• Banking + Bank recon (CSV import)</li>
                  <li>• Reports, AI Insights, Analytics</li>
                </ul>
              </button>

              <button type="button" disabled={busy}
                      data-testid="workspace-type-business"
                      onClick={() => pickEdition("business")}
                      className="group text-left rounded-2xl border-2 border-[var(--border)] hover:border-[var(--brand)] hover:bg-[var(--brand-light)] p-6 transition-colors disabled:opacity-60">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-[var(--brand-light)] group-hover:bg-white text-[var(--brand)] flex items-center justify-center">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted-text)]">Edition</div>
                    <div className="text-lg font-display">Business</div>
                  </div>
                </div>
                <p className="text-sm text-[var(--muted-text)] mb-3 leading-relaxed">
                  Full accountant-grade suite — invoices, bills, payroll, inventory, statements you can hand to your CPA.
                </p>
                <ul className="text-xs text-[var(--text)] space-y-1.5">
                  <li>• Customers & Invoices (AR) · Vendors & Bills (AP)</li>
                  <li>• Payroll · Inventory · Fixed Assets & Tax</li>
                  <li>• Bank recon w/ statement import + auto-match</li>
                  <li>• P&L / BS / CF · Executive KPIs · RBAC team</li>
                </ul>
              </button>
            </div>

            <p className="text-[11px] text-[var(--muted-text)] text-center mt-4">
              Not sure? Pick Personal — you can switch to Business later without losing anything.
            </p>
          </>
        ) : (
          <IndustryStep industries={industries} busy={busy}
                        onBack={() => setStep("edition")}
                        onPick={(industry) => finish({ workspace_type: "business", industry })} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function IndustryStep({ industries, busy, onBack, onPick }) {
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button onClick={onBack} disabled={busy}
                  data-testid="workspace-industry-back"
                  className="text-[var(--muted-text)] hover:text-[var(--brand)] transition">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <DialogTitle className="text-2xl">Pick your industry</DialogTitle>
        </div>
        <DialogDescription>
          We'll seed your Chart of Accounts with the right accounts for this industry. You can add, rename, or delete any of them later — nothing here is locked.
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 max-h-[420px] overflow-y-auto pr-1"
           data-testid="industry-grid">
        {industries.map((it) => (
          <button key={it.key} disabled={busy}
                  data-testid={`industry-card-${it.key}`}
                  onClick={() => onPick(it.key)}
                  className="text-left rounded-xl border-2 border-[var(--border)] hover:border-[var(--brand)] hover:bg-[var(--brand-light)] p-4 transition-colors disabled:opacity-60 group">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-[var(--brand)] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm truncate">{it.label}</div>
                <div className="text-[11px] text-[var(--muted-text)] mt-0.5 leading-snug line-clamp-3">{it.description}</div>
                <div className="text-[10px] text-[var(--muted-text)] mt-2">{it.account_count} accounts</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3">
        <p className="text-[11px] text-[var(--muted-text)]">
          Unsure? Click <span className="font-semibold">Generic</span> — you can apply any industry template later from the Chart of Accounts page.
        </p>
      </div>
    </>
  );
}
